/* global ga */
import React, { Suspense, useEffect } from "react";
import { isNil } from "ramda";
import { compose } from "redux";
import { hot } from "react-hot-loader/root";
import { Provider } from "react-redux";
import { Redirect, Router, Switch, Route } from "react-router-dom";
import { SnackbarProvider } from "notistack";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import DayJsUtils from "@date-io/dayjs";

import { LOCAL_STORAGE_AUTH_KEY } from "utils/localStorage";
import UserLayout from "layouts/UserLayout";
import NoLayout from "layouts/NoLayout";
import CookieBanner from "components/CookieBanner";
import {
  MakeView,
  MakePreview,
  Contentful,
  TemplateGroup,
  Categories,
  Category,
  Home,
  Login,
  NewCast,
  People,
  MyEcards,
  Overview,
  ChangeEmail,
  ChangeName,
  ChangePassword,
  Receipts,
  Receipt,
  SubscriptionDetails,
  EditRenewal,
  UpdateCreditCard,
  Legal,
  AboutUs,
  Privacy,
  TermsOfSale,
  TermsOfService,
  SignUp,
  ResetPassword,
  ResetPasswordReturn,
  LoginHelp,
  Payment,
  PaypalConfirmation,
  PurchaseConfirmation,
  Face,
  Search,
  Page404,
  Unauthorized,
} from "pages";
import ThemeProvider from "layouts/ThemeProvider";
import LoaderBricks from "components/LoaderBricks";
import history from "utils/history";
import localStorage, { RECIPIENT_SESSION_KEY } from "utils/localStorage";
import {
  initGAData,
  trackPageView,
  trackPageViewRegister,
  trackPageViewTemplate,
  trackPageViewCast,
  trackPageViewMake,
  trackPageViewMakePreview,
} from "utils/gAnalyticsUtils";
import store from "store";
import { appRoutes } from "routes";
import {
  useUsers,
  useFaces,
  useOffers,
  useLoading,
  useRouter,
  useScrollToTopOnPathnameChange,
  useTemplateGroup,
  useMakes,
} from "hooks";
import UserPresenter from "presenters/UserPresenter";
import { storeTestEventCode } from "tracking/facebook/conversion";

import { jjLogger } from "./utils/logUtils";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { registerServiceWorker } from "./serviceWorkerRegistration";

const Routes = () => {
  const { setCurrentOffer } = useOffers();
  const { currentUser, signOut } = useUsers();
  const { location } = useRouter();

  const { templateGroup } = useTemplateGroup();
  const { totalFacesCount } = useFaces();
  const { make } = useMakes();

  useScrollToTopOnPathnameChange();

  useEffect(() => {
    storeTestEventCode();
  }, []);

  useEffect(() => {
    ga((tracker) => {
      const clientId = tracker.get("clientId");
      const randomSessionId = `${new Date().getTime()}.${Math.random()
        .toString(36)
        .substring(5)}`;
      initGAData(clientId, randomSessionId);
    });
  }, []);

  useEffect(() => {
    setCurrentOffer();
  }, []);

  useEffect(() => {
    const path = location.pathname + location.search;
    const isViewMakePage = /^\/view\/make\/*\/*\//.test(path);
    if (!localStorage.hasItem(RECIPIENT_SESSION_KEY) && !isViewMakePage) {
      localStorage.setItem(RECIPIENT_SESSION_KEY, "no");
    }
  }, []);

  useEffect(() => {
    const path = location.pathname + location.search;
    const skipPath =
      /^\/not-found/.test(path) ||
      /^\/view\/template\/.+$/.test(path) ||
      /^\/view\/make\/*\/*\//.test(path) ||
      /^\/face/.test(path);

    if (skipPath) return;

    if (/^\/create\/account/.test(path)) {
      trackPageViewRegister(currentUser);
    } else if (/^\/create\/cast\/.+$/.test(path)) {
      trackPageViewCast(currentUser, totalFacesCount);
    } else {
      trackPageView(currentUser);
    }
  }, [location]);

  useEffect(() => {
    if (isNil(templateGroup)) return;
    const path = location.pathname + location.search;

    if (/^\/view\/template\/.+$/.test(path)) {
      trackPageViewTemplate(currentUser, templateGroup);
    } else if (/^\/view\/make-preview\/$/.test(path)) {
      trackPageViewMakePreview(currentUser, templateGroup);
    }
  }, [location, templateGroup]);

  useEffect(() => {
    if (isNil(templateGroup) || isNil(make)) return;
    const path = location.pathname + location.search;

    if (/^\/view\/make\/*\/*\//.test(path)) {
      trackPageViewMake(currentUser, make, templateGroup);
    }
  }, [location, templateGroup, make]);

  window.addEventListener("storage", (e) => {
    if (e.key === LOCAL_STORAGE_AUTH_KEY && e.oldValue && !e.newValue) {
      signOut();
    }
  });

  window.addEventListener("error", (e) => {
    jjLogger.log(e.message);
  });

  window.addEventListener("beforeunload", () => {
    jjLogger.flushLogs();
  });

  return (
    <Suspense fallback={<LoaderBricks />}>
      <Switch>
        <Route exact path={[appRoutes.searchPath()]}>
          <NoLayout>
            <Switch>
              <Route path={appRoutes.searchPath()} component={Search} />
            </Switch>
          </NoLayout>
        </Route>
        <Route path=":path?">
          <UserLayout>
            <Switch>
              <Route exact path={appRoutes.rootPath()} component={Home} />
              <Route exact path={appRoutes.categoriesPath()} component={Categories} />
              <Route path={appRoutes.categoryPath(":slug")} component={Category} />
              <Route path={appRoutes.templateGroupPath(":slug")} component={TemplateGroup} />
              <Route path={appRoutes.contentfulPagePath(":slug")} component={Contentful} />
              <Route path={appRoutes.newCastPath(":slug")} component={NewCast} />
              <Route path={appRoutes.facePath()} component={Face} />
              <Route exact path={appRoutes.termsOfSalePath()} component={TermsOfSale} />
              <Route exact path={appRoutes.termsOfServicePath()} component={TermsOfService} />
              <Route exact path={appRoutes.privacyPath()} component={Privacy} />
              <Route exact path={appRoutes.legalPath()} component={Legal} />
              <Route exact path={appRoutes.aboutUsPath()} component={AboutUs} />
              <Route path={appRoutes.page404Path()} component={Page404} />
              <Route path={appRoutes.makeViewPath(":slug", ":id")} component={MakeView} />
              <Route path={appRoutes.makePreviewPath(":slug", ":id")} component={MakePreview} />
              <Route exact path={appRoutes.unauthorizedPath()} component={Unauthorized} />

              {UserPresenter.notRegistered(currentUser) && (
                <Switch>
                  <Redirect
                    from="/account"
                    to={{ pathname: appRoutes.loginPath(), state: { referrer: location.pathname } }}
                  />

                  <Route path={appRoutes.loginPath()} component={Login} />
                  <Route exact path={appRoutes.signUpPath()} component={SignUp} />
                  <Route path={appRoutes.resetPasswordPath()} component={ResetPassword} />
                  <Route path={appRoutes.resetPasswordReturnPath()} component={ResetPasswordReturn} />
                  <Route path={appRoutes.loginHelpPath()} component={LoginHelp} />
                  <Redirect to={{ pathname: appRoutes.page404Path(), state: { referrer: location.pathname } }} />
                </Switch>
              )}

              {UserPresenter.isRegistered(currentUser) && (
                <Switch>
                  <Redirect from={appRoutes.loginPath()} to={appRoutes.rootPath()} />
                  {!make && <Redirect from={appRoutes.signUpPath()} to={appRoutes.rootPath()} />}
                  {make && !UserPresenter.isPaid(currentUser) && (
                    <Route exact path={appRoutes.signUpPath()} component={SignUp} />
                  )}
                  <Redirect from={appRoutes.resetPasswordPath()} to={appRoutes.rootPath()} />
                  <Redirect from={appRoutes.resetPasswordReturnPath()} to={appRoutes.rootPath()} />
                  <Redirect from={appRoutes.loginHelpPath()} to={appRoutes.rootPath()} />

                  <Route path={appRoutes.myAccountPath()} component={Overview} />
                  <Route path={appRoutes.changeNamePath()} component={ChangeName} />
                  <Route path={appRoutes.changeEmailPath()} component={ChangeEmail} />
                  <Route path={appRoutes.changePasswordPath()} component={ChangePassword} />
                  <Route path={appRoutes.receiptsPath()} component={Receipts} />
                  <Route path={appRoutes.receiptPath(":id")} component={Receipt} />
                  <Route path={appRoutes.subscriptionDetailsPath()} component={SubscriptionDetails} />
                  <Route path={appRoutes.editRenewalPath()} component={EditRenewal} />
                  <Route path={appRoutes.myEcardsPath()} component={MyEcards} />
                  <Route path={appRoutes.peoplePath()} component={People} />
                  <Route path={appRoutes.paypalConfirmationPath()} component={PaypalConfirmation} />

                  {UserPresenter.isPaid(currentUser) && (
                    <Switch>
                      <Redirect from={appRoutes.paymentPath()} to={appRoutes.rootPath()} />

                      <Route path={appRoutes.updateCreditCardPath()} component={UpdateCreditCard} />
                      <Route path={appRoutes.purchaseConfirmationPath()} component={PurchaseConfirmation} />
                    </Switch>
                  )}

                  <Route path={appRoutes.paymentPath()} component={Payment} />
                  <Redirect from={appRoutes.purchaseConfirmationPath()} to={appRoutes.rootPath()} />
                  <Redirect from={appRoutes.paypalConfirmationPath()} to={appRoutes.rootPath()} />
                  <Redirect to={{ pathname: appRoutes.page404Path(), state: { referrer: location.pathname } }} />
                </Switch>
              )}
              <Redirect to={{ pathname: appRoutes.page404Path(), state: { referrer: location.pathname } }} />
            </Switch>
          </UserLayout>
        </Route>
      </Switch>
    </Suspense>
  );
};

const UserApp = () => {
  const { loadCurrentUser } = useUsers();
  const { loadFaces } = useFaces();

  const loadData = () => Promise.all([loadCurrentUser(), loadFaces()]);

  const { func: loadDataWithLoading, isFinished } = useLoading(loadData);

  useEffect(() => {
    registerServiceWorker();
  }, []);

  useEffect(() => {
    loadDataWithLoading();
  }, []);

  return isFinished ? (
    <MuiPickersUtilsProvider utils={DayJsUtils}>
      <SnackbarProvider maxSnack={3}>
        <ThemeProvider>
          <Router history={history}>
            <CookieBanner />
            <Routes />
          </Router>
        </ThemeProvider>
      </SnackbarProvider>
    </MuiPickersUtilsProvider>
  ) : (
    <LoaderBricks />
  );
};

const addStore = (ProxyComponent) => (props) => (
  <Provider store={store}>
    <ProxyComponent {...props} />
  </Provider>
);

UserApp.propTypes = {};

Routes.propTypes = {};

export default compose(hot, addStore)(UserApp);
